import React from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/20/solid';
import { formatCurrency } from '../../../utils/baseUtils';
import NoData2 from '../../../assets/no-data-2.svg';
import { EmptyState, TWDataTable, UserAvatar } from '../../../components/ui';
import { headerCls, DataTableSkeleton } from '../../../data/columns';

function ProductCell({ product }) {
  return (
    <div className="flex items-center gap-3">
      {product?.imagepath ? (
        <img
          alt=""
          className="h-10 w-10 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
          src={product.imagepath}
        />
      ) : (
        <UserAvatar name={product?.name || '?'} size="md" />
      )}
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
          {product?.name || '—'}
        </p>
      </div>
    </div>
  );
}

function SortableTitle({ label, columnKey, sortConfig, onSort, align }) {
  const isActive = sortConfig?.key === columnKey;
  const justify = align === 'right' ? 'justify-end' : 'justify-start';
  return (
    <button
      type="button"
      onClick={() => onSort && onSort(columnKey)}
      className={`inline-flex w-full select-none items-center gap-1 ${justify} ${headerCls} cursor-pointer`}
    >
      {label}
      {isActive && (
        <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-blue-500/10 text-blue-600 dark:bg-sky-400/10 dark:text-sky-300">
          {sortConfig.orderby === 1 ? (
            <ChevronUpIcon className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <ChevronDownIcon className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </span>
      )}
    </button>
  );
}

export default function ProductsTable({
  items,
  loading,
  pagination,
  sortConfig,
  onSort,
  onPageChange,
}) {
  if (loading) {
    return (
      <DataTableSkeleton
        headers={[
          { label: 'Product', w: 'w-40' },
          { label: 'Quantity', align: 'right', w: 'w-16' },
          { label: 'Total', align: 'right', w: 'w-20' },
        ]}
      />
    );
  }

  if (!items || items.length === 0) {
    return (
      <EmptyState
        image={NoData2}
        title="No products to show"
        description="Try a different date range or store."
      />
    );
  }

  const columns = [
    {
      title: <span className={headerCls}>Product</span>,
      key: 'product.name',
      render: (_, item) => <ProductCell product={item.product} />,
    },
    {
      title: (
        <SortableTitle
          label="Quantity"
          columnKey="qty"
          sortConfig={sortConfig}
          onSort={onSort}
          align="right"
        />
      ),
      key: 'qty',
      align: 'right',
      render: (_, item) => (
        <span className="text-sm font-semibold tabular-nums text-slate-700 dark:text-slate-200">
          {item.qty}
        </span>
      ),
    },
    {
      title: (
        <SortableTitle
          label="Total"
          columnKey="total"
          sortConfig={sortConfig}
          onSort={onSort}
          align="right"
        />
      ),
      key: 'total',
      align: 'right',
      render: (_, item) => (
        <span className="text-sm font-bold tabular-nums text-slate-900 dark:text-slate-100">
          {formatCurrency(item.total)}
        </span>
      ),
    },
  ];

  return (
    <div className="reveal">
      <TWDataTable
        columns={columns}
        data={items}
        rowKey={(record, idx) => record.id || idx}
        pagination={
          pagination?.totalRecords > 0
            ? {
                current: pagination.pageNumber,
                pageSize: pagination.pageSize,
                total: pagination.totalRecords,
                showTotal: (total, range) =>
                  `Showing ${range[0]} to ${range[1]} of ${total} products`,
                onChange: onPageChange,
                showSizeChanger: false,
              }
            : undefined
        }
      />
    </div>
  );
}
